<script setup lang="ts">
type Props = {
  selectedDate: string;
  availableDates: string[];
};

type Emits = {
  (e: 'select', date: string): void;
};

const { selectedDate, availableDates } = defineProps<Props>();
const emit = defineEmits<Emits>();

function parseIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);

  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

function isoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
}

const todayIso = computed(() => isoDate(new Date()));

const selectedIndex = computed(() => availableDates.indexOf(selectedDate));

const canGoPrev = computed(() => selectedIndex.value > 0);

const canGoNext = computed(
  () =>
    selectedIndex.value >= 0 && selectedIndex.value < availableDates.length - 1,
);

const visibleDays = computed(() => {
  if (availableDates.length === 0) return [];

  const total = availableDates.length;
  const idx = selectedIndex.value < 0 ? 0 : selectedIndex.value;

  let start = idx - 1;
  let end = idx + 1;

  if (start < 0) {
    end += -start;
    start = 0;
  }
  if (end > total - 1) {
    start -= end - (total - 1);
    end = total - 1;
  }
  start = Math.max(0, start);

  return availableDates.slice(start, end + 1).map((iso) => {
    const d = parseIso(iso);
    const isToday = iso === todayIso.value;
    const weekday = d
      .toLocaleDateString('pt-BR', { weekday: 'short' })
      .replace('.', '');

    return {
      iso,
      isToday,
      weekday: isToday
        ? 'Hoje'
        : weekday.charAt(0).toUpperCase() + weekday.slice(1),
      day: `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`,
      isSelected: iso === selectedDate,
    };
  });
});

function go(delta: number) {
  if (availableDates.length === 0) return;

  const idx = selectedIndex.value < 0 ? 0 : selectedIndex.value;
  const next = idx + delta;

  if (next < 0 || next > availableDates.length - 1) return;

  const target = availableDates[next];

  if (target) emit('select', target);
}

function onSelect(iso: string) {
  if (iso === selectedDate) return;
  emit('select', iso);
}

function goToToday() {
  if (availableDates.length === 0) return;

  const target = availableDates.includes(todayIso.value)
    ? todayIso.value
    : availableDates[0]!;

  onSelect(target);
}
</script>

<template>
  <div :class="$style.nav">
    <NuxtLink
      :class="$style.liveBtn"
      :to="localeLink('/sports')"
    >
      <svg
        fill="none"
        height="18"
        viewBox="0 0 31 18"
        width="31"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          height="16"
          stroke="white"
          stroke-width="2"
          width="29"
          x="1"
          y="1"
        />
        <path
          d="M5.64871 13V5.55H7.00871V11.8H10.1987V13H5.64871ZM11.0784 13V5.55H12.4384V13H11.0784ZM15.8614 13L13.2614 5.55H14.7414L16.7814 11.72H16.4914L18.5314 5.55H20.0114L17.4214 13H15.8614ZM20.844 13V5.55H25.794V6.75H22.204V8.66H25.594V9.86H22.204V11.8H25.794V13H20.844Z"
          fill="white"
        />
      </svg>
    </NuxtLink>

    <div :class="$style.center">
      <button
        :class="[$style.arrow, !canGoPrev && $style.arrowDisabled]"
        :disabled="!canGoPrev"
        type="button"
        @click="go(-1)"
      >
        «
      </button>

      <div :class="$style.days">
        <button
          v-for="d in visibleDays"
          :key="d.iso"
          :class="[$style.day, d.isSelected && $style.active]"
          type="button"
          @click="onSelect(d.iso)"
        >
          <span :class="$style.weekday">{{ d.weekday }}</span>
          <span :class="$style.dayNumber">{{ d.day }}</span>
        </button>
      </div>

      <button
        :class="[$style.arrow, !canGoNext && $style.arrowDisabled]"
        :disabled="!canGoNext"
        type="button"
        @click="go(1)"
      >
        »
      </button>
    </div>

    <button
      :class="$style.calendarBtn"
      type="button"
      @click="goToToday"
    >
      <svg
        :class="$style.calendarIcon"
        fill="none"
        viewBox="0 0 20 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.14286 0V3H12.8571V0H15.7143V3H20V7.5H0V3H4.28571V0H7.14286ZM0 9H20V24H0V9ZM2.85714 15H5.71429V12H2.85714V15ZM8.57143 12V15H11.4286V12H8.57143ZM17.1429 15V12H14.2857V15H17.1429ZM2.85714 21H5.71429V18H2.85714V21ZM11.4286 18H8.57143V21H11.4286V18ZM14.2857 21H17.1429V18H14.2857V21Z"
          fill="white"
        />
      </svg>
    </button>
  </div>
</template>

<style module lang="postcss">
.nav {
  @apply flex justify-between items-center gap-2 mb-3 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-3;

  .liveBtn {
    @apply flex items-center justify-center w-9 h-9 rounded-md transition-colors lg:justify-self-start;

    &:hover {
      background: rgba(255, 255, 255, 0.06);
    }
  }

  .center {
    @apply flex items-center justify-center gap-2 px-[15px] py-[7px] lg:gap-3 lg:[grid-column:2] lg:justify-self-center;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 93.13%,
      rgba(0, 0, 0, 0.1) 100%
    );

    @media (min-width: 1024px) {
      @apply p-0;
      border: none;
      background: none;
      border-radius: 0;
    }

    .arrow {
      @apply w-7 h-7 rounded-md flex items-center justify-center text-white/70 text-base font-bold transition-colors;

      &:hover:not(.arrowDisabled) {
        @apply text-white;
        background: rgba(255, 255, 255, 0.06);
      }

      &.arrowDisabled {
        @apply opacity-30 cursor-not-allowed;
      }
    }

    .days {
      @apply flex items-stretch gap-2 lg:gap-6;

      .day {
        @apply flex flex-col items-center justify-center px-2 transition-colors relative;

        .weekday {
          @apply text-[12px] font-semibold leading-tight text-white/70;
        }

        .dayNumber {
          @apply text-[12px] leading-tight text-white/70;
        }

        &.active {
          .weekday {
            @apply text-white;
          }

          .dayNumber {
            @apply text-white;
          }

          &::after {
            content: '';
            @apply absolute left-1/2 -translate-x-1/2;
            bottom: -4px;
            height: 4px;
            width: 100%;
            border-radius: 9px 9px 0 0;
            background: #ff3400;
            box-shadow: 0 -2px 24px 0 #ff3400;
          }
        }

        &:hover:not(.active) {
          .weekday,
          .dayNumber {
            @apply text-white;
          }
        }
      }
    }
  }

  .calendarBtn {
    @apply w-7 h-7 lg:w-9 lg:h-9 rounded-md flex items-center justify-center text-white/80 transition-colors lg:justify-self-end;

    &:hover {
      @apply text-white;
      background: rgba(255, 255, 255, 0.06);
    }

    .calendarIcon {
      width: 17px;
      height: 21px;

      @media (min-width: 1024px) {
        width: 20px;
        height: 24px;
      }
    }
  }
}
</style>
